import { useState, useEffect, useCallback } from "react";
import apiRequest from "../../../../utils/apiClient";

export function useLibraryTabData<T>(
  url: string,
  messageStore: any,
  entityLabel: string,
) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (silent = false): Promise<void> => {
    // Only the visible (non-silent) load clears the screen / shows a spinner.
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await apiRequest(url);
      if (!response.ok)
        throw new Error(`Failed to fetch ${entityLabel}: ${response.status}`);
      const result: T[] = await response.json();
      setItems(result.filter((item: any) => item.id));
      setLoading(false);
      setError(null); // a good response clears any earlier error
    } catch (err) {
      setLoading(false);
      // CRITICAL: a background poll (silent) must NEVER wipe the list or flash an
      // error on a transient network blip. Keep what's already on screen and just
      // try again next tick. Only the visible load surfaces an error to the user.
      if (silent) {
        console.warn(`Background refresh of ${entityLabel} failed; keeping existing data.`, err);
        return;
      }
      const msg =
        err instanceof Error ? err.message : `Failed to load ${entityLabel}`;
      setError(msg);
      if (!msg.includes("404")) {
        messageStore.setMessage(
          `Failed to load ${entityLabel}. Please try again.`,
          "error",
        );
      } else {
        console.error("API endpoint not found:", url);
      }
    }
  }, [url, messageStore, entityLabel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-recover from a failed initial load: silently retry a few times so a
  // transient blip on first paint heals itself instead of leaving a dead error.
  const [retryTick, setRetryTick] = useState<number>(0);
  useEffect(() => {
    if (error && items.length === 0 && retryTick < 5) {
      const t = setTimeout(() => {
        setRetryTick((n) => n + 1);
        fetchData(true);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [error, items.length, retryTick, fetchData]);

  return { items, setItems, loading, error, silentRefetch: () => fetchData(true) };
}
